import React, { useState, useRef, useEffect, act } from 'react';
import TopNavbar from '../../../components/TopNavbar';
import SideNavbar from '../../../components/SideNavbar';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, startAfter, limit, Timestamp, QueryConstraint, doc, getDoc, updateDoc, getCountFromServer, DocumentSnapshot, limitToLast, endBefore, addDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import { format, startOfDay, endOfDay } from 'date-fns';
import Toast from '../../../components/Toast';

// redux
import { useAppSelector } from "../../../redux/store";
import { ref } from 'firebase/storage';
import GradientSubmitButton from '../../../components/GradientSubmitButton';

interface FollowUp {
    id: string;
    followUp: string;
    followUpTime: Timestamp;
    isDone: boolean;
    isEdited?: boolean;
    leadId: string;
    studentName: string;
    studentNumber: string;
    userId: string;
    assignedUserName: string;
    assignedUserId: string;
    assignedUserNameFromLead: string;
    leadStage: string;
    isActive?: boolean;
}

interface Lead {
    name: string;
    number: string;
    stage?: string;
    leadStage?: string;
    stageId?: string;
    currentStage?: string;
    [key: string]: any; // Allow for any additional fields
}

interface UserData {
    companyId: string;
    isAdmin: boolean;
    isManager: boolean;
    isStaff: boolean;
    name: string;
}

const FollowUps = () => {
    const currentScreen = useAppSelector(state => state.screen.currentScreen);
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
    const [followUps, setFollowUps] = useState<FollowUp[]>([]);
    const [loading, setLoading] = useState(false);
    const [firstVisible, setFirstVisible] = useState<DocumentSnapshot | null>(null);
    const [lastVisible, setLastVisible] = useState<any>(null);
    const [hasMore, setHasMore] = useState(true);

    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(() => {
        const storedPage = sessionStorage.getItem('followUpsCurrentPage');
        return storedPage ? parseInt(storedPage, 10) : 1;
    });
    const [lastVisibleStack, setLastVisibleStack] = useState<DocumentSnapshot[]>([]);
    const [lastPageSize, setLastPageSize] = useState(1);


    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [customDateRange, setCustomDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
    const [appliedDateRange, setAppliedDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
    const [isAdmin, setIsAdmin] = useState(false);
    const [isManager, setIsManager] = useState(false);
    const [isStaff, setIsStaff] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUp | null>(null);
    const [editedFollowUp, setEditedFollowUp] = useState('');
    const [editedDateTime, setEditedDateTime] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [followUpToMarkDone, setFollowUpToMarkDone] = useState<FollowUp | null>(null);
    const [showingToday, setShowingToday] = useState(false);
    const [showingOverdue, setShowingOverdue] = useState(false);
    const [filter, setFilter] = useState<string>(() => {
        const storedFilter = sessionStorage.getItem('followUpsFilter');
        return storedFilter || 'today';
    });
    const [expandedFollowUps, setExpandedFollowUps] = useState<Set<string>>(new Set());

    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const { currentUser } = useAuth();

    const filterPopupRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Pagination persistence helpers
    const savePaginationState = (page: number, lastVisibleDoc?: DocumentSnapshot | null, stackData?: DocumentSnapshot[]) => {
        sessionStorage.setItem('followUpsCurrentPage', page.toString());
        if (lastVisibleDoc) {
            // Store a serializable representation of the document
            const docData = {
                id: lastVisibleDoc.id,
                data: lastVisibleDoc.data(),
                ref: lastVisibleDoc.ref.path
            };
            sessionStorage.setItem('followUpsLastVisible', JSON.stringify(docData));
        }
        if (stackData && stackData.length > 0) {
            const serializedStack = stackData.map(doc => ({
                id: doc.id,
                data: doc.data(),
                ref: doc.ref.path
            }));
            sessionStorage.setItem('followUpsLastVisibleStack', JSON.stringify(serializedStack));
        }
    };

    const clearPaginationState = () => {
        sessionStorage.removeItem('followUpsCurrentPage');
        sessionStorage.removeItem('followUpsLastVisible');
        sessionStorage.removeItem('followUpsLastVisibleStack');
    };

    const restorePaginationStateAndFetch = async () => {
        if (!currentUser || !userData || loading) return;

        const storedPage = sessionStorage.getItem('followUpsCurrentPage');
        const storedPageNumber = storedPage ? parseInt(storedPage, 10) : 1;
        
        if (storedPageNumber === 1) {
            // If we're on page 1, just do a normal fetch
            await fetchFollowUps('init');
            return;
        }

        try {
            setLoading(true);
            
            // We need to navigate to the stored page by fetching pages sequentially
            // This ensures we rebuild the lastVisibleStack correctly
            let currentPageNumber = 1;
            let currentLastVisible: DocumentSnapshot | null = null;
            let currentStack: DocumentSnapshot[] = [];

            while (currentPageNumber <= storedPageNumber) {
                const followUpsRef = collection(db, `companies/${userData.companyId}/followUps`);
                
                // Build the same query conditions as fetchFollowUps
                let conditions: QueryConstraint[] = [
                    where('isDone', '==', false),
                    orderBy('followUpTime', 'asc'),
                    limit(pageSize)
                ];

                // Apply time-based filter conditions
                const now = new Date();
                if (filter === 'today') {
                    const todayStart = startOfDay(now);
                    const todayEnd = endOfDay(now);
                    conditions.push(
                        where('followUpTime', '>=', Timestamp.fromDate(todayStart)),
                        where('followUpTime', '<=', Timestamp.fromDate(todayEnd))
                    );
                } else if (filter === 'upcoming') {
                    const todayEnd = endOfDay(now);
                    conditions.push(where('followUpTime', '>', Timestamp.fromDate(todayEnd)));
                } else if (filter === 'overdue') {
                    const todayStart = startOfDay(now);
                    conditions.push(where('followUpTime', '<', Timestamp.fromDate(todayStart)));
                }

                // Apply custom date range if set
                if (appliedDateRange.start && appliedDateRange.end) {
                    const startDate = startOfDay(appliedDateRange.start);
                    const endDate = endOfDay(appliedDateRange.end);
                    conditions.push(
                        where('followUpTime', '>=', Timestamp.fromDate(startDate)),
                        where('followUpTime', '<=', Timestamp.fromDate(endDate))
                    );
                }

                // Apply student or assignee filters if set
                if (selectedStudentId) {
                    conditions.push(where('leadId', '==', selectedStudentId));
                } else if (selectedAssigneeId) {
                    conditions.push(where('userId', '==', selectedAssigneeId));
                } else {
                    // Apply role-based filtering
                    if (userData.isAdmin && currentScreen === 'Admin') {
                        // Admins see all follow-ups
                    } else if (userData.isManager && currentScreen === 'Manager') {
                        const usersQuery = query(
                            collection(db, 'users'),
                            where('companyId', '==', userData.companyId),
                            where("managerIds", "array-contains", currentUser?.uid)
                        );
                        const usersSnapshot = await getDocs(usersQuery);
                        const users = usersSnapshot.docs.map(doc => doc.id);
                        conditions.push(where('userId', 'in', users));
                    } else {
                        conditions.push(where('userId', '==', currentUser.uid));
                    }
                }

                // Add pagination if not on first page
                if (currentPageNumber > 1 && currentLastVisible) {
                    conditions.push(startAfter(currentLastVisible));
                }

                // Get total count on first iteration
                if (currentPageNumber === 1) {
                    const countConditions = conditions.filter(c =>
                        !['limit', 'startAfter', 'limitToLast'].includes(c.type)
                    );
                    const countQuery = query(followUpsRef, ...countConditions);
                    const countSnapshot = await getCountFromServer(countQuery);
                    setTotalCount(countSnapshot.data().count);
                }

                // Execute query
                const followUpsQuery = query(followUpsRef, ...conditions);
                const snapshot = await getDocs(followUpsQuery);

                if (!snapshot.empty) {
                    if (currentPageNumber > 1) {
                        currentStack.push(currentLastVisible!);
                    }
                    
                    currentLastVisible = snapshot.docs[snapshot.docs.length - 1];
                    
                    // If this is our target page, set the data
                    if (currentPageNumber === storedPageNumber) {
                        setFirstVisible(snapshot.docs[0]);
                        setLastVisible(currentLastVisible);
                        setLastVisibleStack(currentStack);
                        setCurrentPage(storedPageNumber);
                        setHasMore(snapshot.docs.length === pageSize);

                        // Process follow-up data
                        const followUpsData = await Promise.all(
                            snapshot.docs.map(docSnapshot =>
                                processFollowUpData(docSnapshot, userData.companyId)
                            )
                        );
                        setFollowUps(followUpsData);
                        break;
                    }
                } else {
                    // No more data, reset to page 1
                    clearPaginationState();
                    await fetchFollowUps('init');
                    break;
                }

                currentPageNumber++;
            }
        } catch (error) {
            console.error('Error restoring pagination state:', error);
            // Fall back to normal fetch on error
            clearPaginationState();
            await fetchFollowUps('init');
        } finally {
            setLoading(false);
        }
    };

    // Page size state with default value of 10
    const [pageSize, setPageSize] = useState<number>(() => {
        const storedPageSize = sessionStorage.getItem('followUpsPageSize');
        return storedPageSize ? parseInt(storedPageSize, 10) : 10;
    });


    const [selectedStudent, setSelectedStudent] = useState<string>('');
    const [selectedStudentInModal, setSelectedStudentInModal] = useState<string>('');
    const [selectedStudentId, setSelectedStudentId] = useState<string>('');
    const [selectedStudentInModalId, setSelectedStudentInModalId] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchTermInModal, setSearchTermInModal] = useState<string>('');
    const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
    const [isInModalStudentDropdownOpen, setIsInModalStudentDropdownOpen] = useState(false);
    const [students, setStudents] = useState<any[]>([]);
    const [isStudentLoading, setIsStudentLoading] = useState(false);

    const [selectedAssignee, setSelectedAssignee] = useState<string>('');
    const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('');
    const [assigneeSearchTerm, setAssigneeSearchTerm] = useState<string>('');
    const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
    const [assignees, setAssignees] = useState<any[]>([]);
    const [isAssigneeLoading, setIsAssigneeLoading] = useState(false);

    // Add follow-up states
    const [showAddFollowUpModal, setShowAddFollowUpModal] = useState(false);
    const [followUpDescription, setFollowUpDescription] = useState('');
    const [followUpDateTime, setFollowUpDateTime] = useState('');
    const [toogleAddFollowUpAction, setToggleAddFollowUpAction] = useState<'add' | 'update'>('add');

    const studentDropdownRef = useRef<HTMLDivElement | null>(null);
    const studentInModalDropdownRef = useRef<HTMLDivElement | null>(null);
    const assigneeDropdownRef = useRef<HTMLDivElement | null>(null);

    // Get user data including company ID
    useEffect(() => {
        const getUserData = async () => {
            if (!currentUser) return;
            try {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const data = userDocSnap.data() as UserData;
                    setUserData(data);
                    setIsAdmin(data.isAdmin);
                    setIsManager(data.isManager);
                    setIsStaff(data.isStaff);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };
        getUserData();
    }, [currentUser]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
                setIsFilterOpen(false);
            }
            if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target as Node)) {
                setIsStudentDropdownOpen(false);
            }
            if (studentInModalDropdownRef.current && !studentInModalDropdownRef.current.contains(event.target as Node)) {
                setIsInModalStudentDropdownOpen(false);
            }
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target as Node)) {
                setIsAssigneeDropdownOpen(false);
            }

        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDropdown = (dropdown: string, forceOpen?: boolean) => {
        if (dropdown === 'student') {
            setIsStudentDropdownOpen((prev) => forceOpen !== undefined ? forceOpen : !prev);
            setIsInModalStudentDropdownOpen(false); // Close the modal dropdown when opening the main dropdown
        } else if (dropdown === 'assignee') {
            setIsAssigneeDropdownOpen((prev) => forceOpen !== undefined ? forceOpen : !prev);
        } else if (dropdown === 'studentInModal') {
            setIsInModalStudentDropdownOpen((prev) => forceOpen !== undefined ? forceOpen : !prev);
            setIsStudentDropdownOpen(false); // Close the main dropdown when opening the modal dropdown
        }
    }

    // student search
    const handleStudentSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        console.log("id:", e.target.id);


        if (e.target.id === 'inPageStudentSearchInput') {
            setSelectedStudent(value);
            setSearchTerm(value);
        } else if (e.target.id === 'inModalStudentSearchInput') {
            setSelectedStudentInModal(value);
            setSearchTermInModal(value);
        }

        // toogle student dropdown
        if (e.target.id === 'inPageStudentSearchInput' && !isStudentDropdownOpen) {
            toggleDropdown("student", true);
        } else if (e.target.id === 'inModalStudentSearchInput' && !isInModalStudentDropdownOpen) {
            toggleDropdown("studentInModal", true);
        }


        if (value.length < 3) {
            setStudents([]);
            return;
        }

        setIsStudentLoading(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            companyId = userDoc.data()?.companyId;

            if (!companyId) return;

            let studentsQuery = query(
                collection(db, `companies/${companyId}/leads`),
                where('name', '>=', value),
                where('name', '<=', value + '\uf8ff'),
                where('isActive', '==', true),
                orderBy('name')
            );

            if (isManager && currentScreen === 'Manager') {
                const usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );

                const usersSnapshot = await getDocs(usersQuery);
                const userIds = usersSnapshot.docs.map(doc => doc.id);
                console.log("userIds:", userIds);


                if (userIds.length === 0) {
                    // toast message
                    setToastMessage("No users found in your team");
                    setShowToast(true);
                }

                studentsQuery = query(
                    collection(db, `companies/${companyId}/leads`),
                    where('name', '>=', value),
                    where('name', '<=', value + '\uf8ff'),
                    where('isActive', '==', true),
                    where('assignedTo', 'in', userIds),
                    orderBy('name')
                );
            } else if (isStaff && currentScreen === 'Staff') {
                studentsQuery = query(
                    collection(db, `companies/${companyId}/leads`),
                    where('name', '>=', value),
                    where('name', '<=', value + '\uf8ff'),
                    where('isActive', '==', true),
                    where('assignedTo', '==', currentUser?.uid),
                    orderBy('name')
                );
            }


            const studentsSnapshot = await getDocs(studentsQuery);
            const studentList = studentsSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            }));

            setStudents(studentList);
        } catch (error) {
            console.error("Error fetching students:", error);
        } finally {
            setIsStudentLoading(false);
        }
    };

    // assignee search
    const handleAssigneeSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setSelectedAssignee(value);
        setAssigneeSearchTerm(value);
        // toogle assignee dropdown
        if (!isAssigneeDropdownOpen) {
            setIsAssigneeDropdownOpen(true);
        }
        if (value.length < 3) {
            setAssignees([]);
            return;
        }
        setIsAssigneeLoading(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) return;
            let companyId: string | undefined;
            // Get user document to get companyId
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            companyId = userDoc.data()?.companyId;
            if (!companyId) return;
            let usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', companyId),
                where('name', '>=', value),
                where('name', '<=', value + '\uf8ff'),
                orderBy('name')
            );

            if (isManager && currentScreen === 'Manager') {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where("managerIds", "array-contains", currentUser?.uid),
                    where('name', '>=', value),
                    where('name', '<=', value + '\uf8ff'),
                    orderBy('name')
                );
            }

            const usersSnapshot = await getDocs(usersQuery);
            const userList = usersSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            }));

            if (userList.length === 0) {
                // toast message
                setToastMessage("No users found in your team");
                setShowToast(true);
            }

            setAssignees(userList);
        } catch (error) {
            console.error("Error fetching assignees:", error);
        } finally {
            setIsAssigneeLoading(false);
        }
    }



    // Clear filters
    const clearFilters = async () => {
        if (!currentUser || !userData || loading) return;

        try {
            setLoading(true);
            // Reset filter states but keep the current filter selection
            setCustomDateRange({ start: null, end: null });
            setAppliedDateRange({ start: null, end: null });
            setIsFilterOpen(false);
            setSelectedStudent('');
            setSelectedStudentId('');
            setSelectedAssignee('');
            setSelectedAssigneeId('');
            setFollowUps([]);
            setLastVisible(null);
            setFirstVisible(null);
            setLastVisibleStack([]);
            setHasMore(true);
            setCurrentPage(1);

            // Clear pagination state from sessionStorage
            clearPaginationState();

            // Reset to default filter and clear stored filter
            const defaultFilter = 'today';
            setFilter(defaultFilter);
            sessionStorage.setItem('followUpsFilter', defaultFilter);

            const followUpsRef = collection(db, `companies/${userData.companyId}/followUps`);
            let conditions: QueryConstraint[] = [
                where('isDone', '==', false),
                orderBy('followUpTime', 'asc'),
                limit(pageSize)
            ];

            // Apply current filter conditions (now default to 'today')
            const now = new Date();
            if (defaultFilter === 'today') {
                // Show all follow-ups from today (start of day to end of day)
                const todayStart = startOfDay(now);
                const todayEnd = endOfDay(now);
                conditions.push(
                    where('followUpTime', '>=', Timestamp.fromDate(todayStart)),
                    where('followUpTime', '<=', Timestamp.fromDate(todayEnd))
                )
            } else if (defaultFilter === 'upcoming') {
                const todayEnd = endOfDay(now);
                conditions.push(where('followUpTime', '>', Timestamp.fromDate(todayEnd)));
            } else if (defaultFilter === 'overdue') {
                // Show follow-ups before now (including today's past follow-ups)
                conditions.push(where('followUpTime', '<', Timestamp.fromDate(now)));
            }

            // Set conditions based on user role
            if (userData.isAdmin && currentScreen === 'Admin') {
                // Admins can view all follow-ups
            } else if (userData.isManager && currentScreen === 'Manager') {
                const usersQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
                const usersSnapshot = await getDocs(usersQuery);
                const users = usersSnapshot.docs.map(doc => doc.id);
                conditions.push(where('userId', 'in', users));
            } else {
                conditions.push(where('userId', '==', currentUser.uid));
            }

            // Get count of filtered follow-ups
            const countQuery = query(followUpsRef, ...conditions.filter((c: QueryConstraint) =>
                !(c.type === 'limit')
            ));
            const countSnapshot = await getCountFromServer(countQuery);
            const total = countSnapshot.data().count;
            setTotalCount(total);

            // Fetch first page
            const followUpsQuery = query(followUpsRef, ...conditions);
            const snapshot = await getDocs(followUpsQuery);

            if (!snapshot.empty) {
                setFirstVisible(snapshot.docs[0]);
                setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            }

            setHasMore(snapshot.docs.length === pageSize);

            // Process follow-up data
            const followUpsData = await Promise.all(
                snapshot.docs.map(docSnapshot => processFollowUpData(docSnapshot, userData.companyId))
            );

            setFollowUps(followUpsData);
            setToastMessage('Filters cleared successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            setToastMessage('Failed to clear filters');
            setIsErrorToast(true);
            setShowToast(true);
            console.error('Error clearing filters:', error);
        } finally {
            setLoading(false);
        }
    };


    // Apply custom date range
    const applyCustomDateRange = async () => {
        if (!currentUser || !userData || loading) return;
        if (customDateRange.start && customDateRange.end) {
            setAppliedDateRange(customDateRange);
            setIsFilterOpen(false);
            setFollowUps([]);
            setLastVisible(null);
            setHasMore(true);
            setCurrentPage(1);
            setLastVisibleStack([]);

            // Clear pagination state when applying new date range
            clearPaginationState();

            const followUpsRef = collection(db, `companies/${userData?.companyId}/followUps`);
            const startDate = startOfDay(customDateRange.start);
            const endDate = endOfDay(customDateRange.end);

            const conditions: QueryConstraint[] = [
                where('followUpTime', '>=', Timestamp.fromDate(startDate)),
                where('followUpTime', '<=', Timestamp.fromDate(endDate)),
                limit(pageSize)
            ];

            if (userData.isAdmin && currentScreen === 'Admin') {
                // Admins can view all follow-ups
                conditions.push(where('isDone', '==', false));
                conditions.push(orderBy('followUpTime', 'asc'));
            } else if (userData.isManager && currentScreen === 'Manager') {

                // find all users that have the same managerId as the current user
                const usersQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
                const usersSnapshot = await getDocs(usersQuery);
                const users = usersSnapshot.docs.map(doc => doc.id);

                if (users.length === 0) {
                    // toast message
                    setToastMessage("No users found in your team");
                    setShowToast(true);
                }

                conditions.push(where('userId', 'in', users));
                conditions.push(where('isDone', '==', false));
                conditions.push(orderBy('followUpTime', 'asc'));
            } else {
                // Default to user's own follow-ups
                conditions.push(where('userId', '==', currentUser.uid));
                conditions.push(where('isDone', '==', false));
                conditions.push(orderBy('followUpTime', 'asc'));
            }
            try {
                setLoading(true);

                // Get total count for the date range (without pagination limits)
                const countConditions = conditions.filter(c =>
                    !['limit', 'startAfter', 'limitToLast'].includes(c.type)
                );
                const countQuery = query(followUpsRef, ...countConditions);
                const countSnapshot = await getCountFromServer(countQuery);
                setTotalCount(countSnapshot.data().count);

                const followUpsQuery = query(followUpsRef, ...conditions);
                const snapshot = await getDocs(followUpsQuery);
                setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
                setHasMore(snapshot.docs.length === pageSize);

                // Use the helper function to process follow-up data
                const followUpsData = await Promise.all(snapshot.docs.map(docSnapshot =>
                    processFollowUpData(docSnapshot, userData?.companyId || '')
                ));
                setToastMessage('Date range applied successfully!');
                setIsErrorToast(false);
                setShowToast(true);
                setFollowUps(followUpsData);
            } catch (error) {
                setToastMessage('Failed to apply date range!');
                setIsErrorToast(true);
                setShowToast(true);
                console.error('Error applying date range:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const fetchFollowUps = async (direction: 'next' | 'prev' | 'first' | 'last' | 'init' = 'init') => {
        if (!currentUser || !userData || loading) return;

        try {
            setLoading(true);
            const followUpsRef = collection(db, `companies/${userData.companyId}/followUps`);

            // Base query conditions
            let conditions: QueryConstraint[] = [
                where('isDone', '==', false),
                orderBy('followUpTime', 'asc'),
                limit(pageSize)
            ];

            // Apply time-based filter conditions
            const now = new Date();
            if (filter === 'today') {
                // Show all follow-ups from today (start of day to end of day)
                const todayStart = startOfDay(now);
                const todayEnd = endOfDay(now);
                conditions.push(
                    where('followUpTime', '>=', Timestamp.fromDate(todayStart)),
                    where('followUpTime', '<=', Timestamp.fromDate(todayEnd))
                );
            } else if (filter === 'upcoming') {
                const todayEnd = endOfDay(now);
                conditions.push(where('followUpTime', '>', Timestamp.fromDate(todayEnd)));
            } else if (filter === 'overdue') {
                // Show follow-ups before today (excluding today's follow-ups to prevent duplication)
                const todayStart = startOfDay(now);
                conditions.push(where('followUpTime', '<', Timestamp.fromDate(todayStart)));
            }

            // Apply custom date range if set
            if (appliedDateRange.start && appliedDateRange.end) {
                const startDate = startOfDay(appliedDateRange.start);
                const endDate = endOfDay(appliedDateRange.end);
                conditions.push(
                    where('followUpTime', '>=', Timestamp.fromDate(startDate)),
                    where('followUpTime', '<=', Timestamp.fromDate(endDate))
                );
            }

            // Apply student or assignee filters if set
            if (selectedStudentId) {
                conditions.push(where('leadId', '==', selectedStudentId));
            } else if (selectedAssigneeId) {
                conditions.push(where('userId', '==', selectedAssigneeId));
            } else {
                // Apply role-based filtering
                if (userData.isAdmin && currentScreen === 'Admin') {
                    // Admins see all follow-ups (already handled by base conditions)
                } else if (userData.isManager && currentScreen === 'Manager') {
                    const usersQuery = query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId),
                        where("managerIds", "array-contains", currentUser?.uid)
                    );
                    const usersSnapshot = await getDocs(usersQuery);
                    const users = usersSnapshot.docs.map(doc => doc.id);
                    conditions.push(where('userId', 'in', users));
                } else {
                    // Staff only see their own follow-ups
                    conditions.push(where('userId', '==', currentUser.uid));
                }
            }

            // Handle pagination
            if (direction === 'next' && lastVisible) {
                conditions.push(startAfter(lastVisible));
            } else if (direction === 'prev' && lastVisibleStack.length > 0) {
                if (currentPage === 2) {
                    // Going back to first page - don't add any startAfter
                    // All necessary conditions are already in the array
                } else if (currentPage > 2) {
                    const prevLastVisible = lastVisibleStack[lastVisibleStack.length - 2];
                    conditions.push(startAfter(prevLastVisible));
                }
            } else if (direction === 'first') {
                // Reset to first page - remove any existing startAfter conditions
                conditions = conditions.filter(c => c.type !== 'startAfter');
            }

            // Get total count (without pagination limits)
            const countConditions = conditions.filter(c =>
                !['limit', 'startAfter', 'limitToLast'].includes(c.type)
            );
            const countQuery = query(followUpsRef, ...countConditions);
            const countSnapshot = await getCountFromServer(countQuery);
            setTotalCount(countSnapshot.data().count);

            // Execute query
            const followUpsQuery = query(followUpsRef, ...conditions);
            const snapshot = await getDocs(followUpsQuery);

            // Update pagination state
            if (!snapshot.empty) {
                const newFirstVisible = snapshot.docs[0];
                const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
                let newCurrentPage = currentPage;
                let newLastVisibleStack = [...lastVisibleStack];

                setFirstVisible(newFirstVisible);
                setLastVisible(newLastVisible);

                if (direction === 'next') {
                    newLastVisibleStack = [...lastVisibleStack, lastVisible];
                    newCurrentPage = currentPage + 1;
                    setLastVisibleStack(newLastVisibleStack);
                    setCurrentPage(newCurrentPage);
                } else if (direction === 'prev') {
                    if (currentPage === 2) {
                        newLastVisibleStack = [];
                        setLastVisibleStack([]);
                    } else {
                        newLastVisibleStack = lastVisibleStack.slice(0, -1);
                        setLastVisibleStack(newLastVisibleStack);
                    }
                    newCurrentPage = currentPage - 1;
                    setCurrentPage(newCurrentPage);
                } else if (direction === 'first') {
                    newLastVisibleStack = [];
                    newCurrentPage = 1;
                    setLastVisibleStack([]);
                    setCurrentPage(1);
                } else if (direction === 'init') {
                    // For init, don't change the current page if it's already set from sessionStorage
                    const storedPage = sessionStorage.getItem('followUpsCurrentPage');
                    if (!storedPage) {
                        newCurrentPage = 1;
                        setCurrentPage(1);
                    }
                }

                // Save pagination state to sessionStorage
                savePaginationState(newCurrentPage, newLastVisible, newLastVisibleStack);
            }

            setHasMore(snapshot.docs.length === pageSize);

            // Process follow-up data
            const followUpsData = await Promise.all(
                snapshot.docs.map(docSnapshot =>
                    processFollowUpData(docSnapshot, userData.companyId)
                )
            );

            setFollowUps(followUpsData);
        } catch (error) {
            console.error('Error fetching follow-ups:', error);
            setToastMessage('Failed to fetch follow-ups');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (
        e?: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLImageElement> | React.MouseEvent<Element, MouseEvent>,
        searchValue?: string // Add this parameter to check if input is empty
    ) => {
        if (!e) {
            fetchFollowUps();
            return;
        }

        // Handle keyboard event (Enter key)
        if ("key" in e && e.key === "Enter") {
            e.preventDefault();
            // Check if search input is empty
            if (!searchValue || searchValue.trim() === "") {
                clearSearch(e as unknown as React.MouseEvent); // Cast to MouseEvent since clearSearch expects it
            } else {
                fetchFollowUps();
            }
        }

        // Handle mouse click event
        if (e.type === "click") {
            e.preventDefault();
            fetchFollowUps();
        }
    }

    const clearSearch = (e: React.MouseEvent) => {
        e.preventDefault();
        setSelectedStudent('');
        setSelectedStudentId('');
        setStudents([]);
        setSearchTerm('');
        setIsStudentDropdownOpen(false);
        setSelectedAssignee('');
        setSelectedAssigneeId('');
        setAssignees([]);
        setAssigneeSearchTerm('');
        setIsAssigneeDropdownOpen(false);
        
        // Clear pagination state when clearing search
        clearPaginationState();
    }

    // Handle page size change
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        sessionStorage.setItem('followUpsPageSize', newPageSize.toString());

        // Clear existing data to prevent confusion when showing a new page size
        setFollowUps([]);

        // Reset pagination state
        setCurrentPage(1);
        setLastVisible(null);
        setFirstVisible(null);
        setLastVisibleStack([]);
        setHasMore(true);

        // Clear pagination state from sessionStorage when page size changes
        clearPaginationState();

        // Trigger a fresh data fetch with new page size
        // The useEffect for fetchFollowUps will handle this automatically
    };

    useEffect(() => {
        fetchFollowUps();
    }, [selectedStudentId, selectedAssigneeId]);

    const handleStudentSelectInModal = async (student: any) => {
        if (!userData) return;

        // Clear previous follow-up data when selecting a new student
        setSelectedFollowUp(null);
        setFollowUpDescription('');
        setFollowUpDateTime('');
        setToggleAddFollowUpAction('add');

        // Set the selected student in modal state
        setSelectedStudentInModalId(student.id);
        setSelectedStudentInModal(student.name);
        setSearchTermInModal('');
        setIsInModalStudentDropdownOpen(false);

        if (student.id) {
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);

            try {
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists() && leadDoc.data()?.hasActiveFollowUp) {
                    // Get the active follow-up ID
                    const activeFollowUpId = leadDoc.data()?.activeFollowUpId;
                    if (activeFollowUpId) {
                        setToastMessage("This student already has an active follow-up. Please complete it before adding a new one.");
                        setIsErrorToast(true);
                        setShowToast(true);

                        // Populate the follow-up description and date time with the active follow-up details
                        const followUpRef = doc(db, `companies/${userData.companyId}/followUps`, activeFollowUpId);
                        const followUpDoc = await getDoc(followUpRef);
                        if (followUpDoc.exists()) {
                            const followUpData = followUpDoc.data();
                            // update followUpData with the followUpDoc id
                            followUpData.id = followUpDoc.id;
                            setSelectedFollowUp(followUpData as FollowUp);
                            setFollowUpDescription(followUpData.followUp || '');
                            setFollowUpDateTime(format(followUpData.followUpTime.toDate(), "yyyy-MM-dd'T'HH:mm"));
                            setToggleAddFollowUpAction('update');
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching lead document:", error);
                setToastMessage("Error checking student follow-up status.");
                setIsErrorToast(true);
                setShowToast(true);
            }
        }
    }

    const handleAddFollowUp = async (action: 'add' | 'update') => {

        if (!followUpDescription.trim() || !followUpDateTime || !userData) {
            setToastMessage('Please fill in all fields!'); setIsErrorToast(true); setShowToast(true);
            return;
        }

        try {
            setLoading(true);
            const followUpRef = collection(db, `companies/${userData.companyId}/followUps`);
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudentInModalId);

            if (!selectedStudentInModalId) {
                setToastMessage('Please select a student!'); setIsErrorToast(true); setShowToast(true);
                return;
            }

            if (action === 'add') {
                // Get the lead's assignedTo value to use as userId
                const leadDoc = await getDoc(leadRef);
                const leadAssignedTo = leadDoc.exists() ? leadDoc.data()?.assignedTo : null;

                if (!leadAssignedTo) {
                    setToastMessage('Error: Lead has no assigned user!'); setIsErrorToast(true); setShowToast(true);
                    setLoading(false);
                    return;
                }

                // Create a new follow-up document
                const newFollowUpTime = Timestamp.fromDate(new Date(followUpDateTime));
                const assignedUserName = userData?.name || 'Unknown';

                const newFollowUp = {
                    followUp: followUpDescription.trim(),
                    followUpTime: newFollowUpTime,
                    isDone: false,
                    isEdited: false,
                    leadId: selectedStudentInModalId,
                    userId: leadAssignedTo,
                    assignedUserName: assignedUserName,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };

                // Add the new follow-up document
                const docRef = await addDoc(followUpRef, newFollowUp);

                // update the leads document to set hasActiveFollowUp to true and activeFollowUpId to the new follow-up id
                await updateDoc(leadRef, {
                    hasActiveFollowUp: true,
                    activeFollowUpId: docRef.id,
                    updatedAt: Timestamp.now(),
                    activeFollowUpTime: newFollowUpTime,
                    activeFollowUpText: followUpDescription.trim()
                });

                setToastMessage('Follow-up added successfully!'); setIsErrorToast(false); setShowToast(true);
            } else if (action === 'update') {
                // Update the existing follow-up document
                const followUpDocRef = doc(db, `companies/${userData.companyId}/followUps`, selectedFollowUp?.id || '');
                const newFollowUpTime = Timestamp.fromDate(new Date(followUpDateTime));
                const assignedUserName = userData?.name || 'Unknown';

                await updateDoc(followUpDocRef, {
                    followUp: followUpDescription.trim(),
                    followUpTime: newFollowUpTime,
                    assignedUserName: assignedUserName,
                    updatedAt: Timestamp.now()
                });

                await updateDoc(leadRef, {
                    hasActiveFollowUp: true,
                    activeFollowUpId: selectedFollowUp?.id,
                    updatedAt: Timestamp.now(),
                    activeFollowUpTime: newFollowUpTime,
                    activeFollowUpText: followUpDescription.trim()
                });

                // Also update the lead's updatedAt timestamp
                await updateDoc(leadRef, {
                    updatedAt: Timestamp.now()
                });

                setToastMessage('Follow-up updated successfully!'); setIsErrorToast(false); setShowToast(true);
            }



            // Reset modal state
            setShowAddFollowUpModal(false);
            setSelectedStudentInModal('');
            setSelectedStudentInModalId('');
            setIsInModalStudentDropdownOpen(false);
            setFollowUpDescription('');
            setFollowUpDateTime('');

            //  call the fetchFollowUps function to refresh the list
            fetchFollowUps('init');

        } catch (error) {
            setToastMessage('Error adding follow-up'); setIsErrorToast(true); setShowToast(true);
            console.error('Error adding follow-up:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleEdit = async () => {
        if (!selectedFollowUp || !editedFollowUp.trim() || !editedDateTime || !userData) {
            setToastMessage('Please fill in all fields!');
            setIsErrorToast(true);
            setShowToast(true);
            return;
        }

        try {
            setLoading(true);

            const newFollowUpTime = Timestamp.fromDate(new Date(editedDateTime));

            // Verify the timestamp is valid
            if (isNaN(newFollowUpTime.toDate().getTime())) {
                setToastMessage('Invalid date/time format!');
                setIsErrorToast(true);
                setShowToast(true);
                return;
            }

            // Step 1: Mark the current follow-up as done and edited
            const currentFollowUpRef = doc(db, `companies/${userData.companyId}/followUps`, selectedFollowUp.id);
            await updateDoc(currentFollowUpRef, {
                isDone: true,
                isEdited: true,
                updatedAt: Timestamp.now()
            });

            // Step 2: Create a new follow-up with the updated details
            const followUpsCollectionRef = collection(db, `companies/${userData.companyId}/followUps`);

            // Get the lead's assignedTo value to use as userId
            const leadDocRef = doc(db, `companies/${userData.companyId}/leads`, selectedFollowUp.leadId);
            const leadDoc = await getDoc(leadDocRef);
            const leadAssignedTo = leadDoc.exists() ? leadDoc.data()?.assignedTo : selectedFollowUp.userId;
            const assignedUserName = userData?.name || 'Unknown';

            const newFollowUp = {
                followUp: editedFollowUp.trim(),
                followUpTime: newFollowUpTime,
                isDone: false,
                isEdited: false,
                leadId: selectedFollowUp.leadId,
                userId: leadAssignedTo, // Use lead's current assignedTo
                assignedUserName: assignedUserName, // Current user who edited the follow-up
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            // Add the new follow-up document
            const newFollowUpDocRef = await addDoc(followUpsCollectionRef, newFollowUp);

            // Step 3: Update the lead document to point to the new active follow-up
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedFollowUp.leadId);
            await updateDoc(leadRef, {
                hasActiveFollowUp: true,
                activeFollowUpId: newFollowUpDocRef.id,
                updatedAt: Timestamp.now(),
                activeFollowUpTime: newFollowUpTime,
                activeFollowUpText: editedFollowUp.trim()
            });

            // Update local state - remove the old follow-up and add the new one
            setFollowUps(currentFollowUps => {
                // Remove the old follow-up
                const filteredFollowUps = currentFollowUps.filter(f => f.id !== selectedFollowUp.id);

                // Add the new follow-up with all necessary fields
                const newFollowUpWithDetails = {
                    id: newFollowUpDocRef.id,
                    followUp: editedFollowUp.trim(),
                    followUpTime: newFollowUpTime,
                    isDone: false,
                    isEdited: false,
                    leadId: selectedFollowUp.leadId,
                    studentName: selectedFollowUp.studentName,
                    studentNumber: selectedFollowUp.studentNumber,
                    userId: selectedFollowUp.userId,
                    assignedUserName: selectedFollowUp.assignedUserName,
                    assignedUserId: selectedFollowUp.assignedUserId,
                    assignedUserNameFromLead: selectedFollowUp.assignedUserNameFromLead,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    leadStage: selectedFollowUp.leadStage
                };

                // Check if the new follow-up should be visible based on current filters
                if (appliedDateRange.start && appliedDateRange.end) {
                    const startDate = startOfDay(appliedDateRange.start);
                    const endDate = endOfDay(appliedDateRange.end);
                    const newDate = newFollowUpTime.toDate();

                    if (newDate < startDate || newDate > endDate) {
                        return filteredFollowUps; // Don't add the new follow-up if it's outside date range
                    }
                }

                return [...filteredFollowUps, newFollowUpWithDetails];
            });

            setToastMessage('Follow-up updated successfully! A new follow-up has been created.');
            setIsErrorToast(false);
            setShowToast(true);

            setShowEditModal(false);
            setSelectedFollowUp(null);
            setEditedFollowUp('');
            setEditedDateTime('');
            window.location.reload();

        } catch (error) {
            console.error('Error updating follow-up:', error);

            // More specific error messages
            if (error instanceof Error) {
                if (error.message.includes('permission')) {
                    setToastMessage('Permission denied: You cannot edit this follow-up');
                } else if (error.message.includes('not-found')) {
                    setToastMessage('Follow-up not found in database');
                } else {
                    setToastMessage('Error updating follow-up: ' + error.message);
                }
            } else {
                setToastMessage('Error updating follow-up');
            }

            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsDone = async () => {
        if (!followUpToMarkDone || !userData) return;

        try {
            setLoading(true);

            // Update followUp document
            const followUpRef = doc(db, `companies/${userData.companyId}/followUps`, followUpToMarkDone.id);
            await updateDoc(followUpRef, {
                isDone: true,
                doneAt: Timestamp.now(), // Track when it was marked done
                doneBy: currentUser?.uid, // Track who marked it done
                updatedAt: Timestamp.now()
            });

            // Update lead document
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, followUpToMarkDone.leadId);
            await updateDoc(leadRef, {
                hasActiveFollowUp: false,
                activeFollowUpId: null,
                lastFollowUpAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                activeFollowUpTime: null,
                activeFollowUpText: null
            });

            // Optimistically update local state
            setFollowUps(prev => prev.filter(f => f.id !== followUpToMarkDone.id));

            // Update total count
            setTotalCount(prev => prev - 1);

            // Reset modal and selection
            setShowConfirmModal(false);
            setFollowUpToMarkDone(null);

            // Show success message
            setToastMessage('Follow-up marked as done successfully!');
            setIsErrorToast(false);
            setShowToast(true);

            // If we're left with less than pageSize items, try to load more
            if (followUps.length <= 1) {
                await fetchFollowUps('init');
            }
        } catch (error) {
            console.error('Error marking follow-up as done:', error);

            // Revert optimistic update
            if (followUpToMarkDone) {
                setFollowUps(prev => [...prev, followUpToMarkDone]);
                setTotalCount(prev => prev + 1);
            }

            setToastMessage('Failed to mark follow-up as done');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    // cpoy the lead's phone number to clipboard
    const copyPhoneNumberToClipboard = (phoneNumber: string) => {
        navigator.clipboard.writeText(phoneNumber)
            .then(() => {
                setToastMessage('Phone number copied to clipboard!'); setIsErrorToast(false); setShowToast(true);
            })
            .catch((error) => {
                setToastMessage('Failed to copy phone number'); setIsErrorToast(true); setShowToast(true);
                console.error('Error copying phone number:', error);
            });
    };

    const openConfirmModal = (followUp: FollowUp) => {
        setFollowUpToMarkDone(followUp);
        setShowConfirmModal(true);
    };

    const openEditModal = (followUp: FollowUp) => {
        console.log('Opening edit modal for:', followUp);

        setSelectedFollowUp(followUp);
        setEditedFollowUp(followUp.followUp);

        // Ensure the date format is correct
        const date = followUp.followUpTime.toDate();
        const formattedDateTime = format(date, "yyyy-MM-dd'T'HH:mm");
        console.log('Formatted date time:', formattedDateTime);

        setEditedDateTime(formattedDateTime);
        setShowEditModal(true);
    };

    // Initial fetch when component mounts or filter changes
    useEffect(() => {
        if (userData) {
            const storedPage = sessionStorage.getItem('followUpsCurrentPage');
            const storedPageNumber = storedPage ? parseInt(storedPage, 10) : 1;
            
            // If we have a stored page > 1, try to restore state, otherwise normal fetch
            if (storedPageNumber > 1) {
                restorePaginationStateAndFetch();
            } else {
                fetchFollowUps('init');
            }
        }
    }, [userData, filter, pageSize]);

    // Load more
    const loadMore = () => {
        if (hasMore && !loading) {
            fetchFollowUps('init');
        }
    };

    const handleStudentClick = (studentId: string) => {
        navigate(`/studentdetails/${studentId}`);
    };

    // Helper function to process follow-up data with user information
    const processFollowUpData = async (docSnapshot: any, companyId: string) => {
        const data = docSnapshot.data();
        const leadDocRef = doc(db, `companies/${companyId}/leads`, data.leadId);
        const leadDoc = await getDoc(leadDocRef);
        const leadData = leadDoc.data() as Lead | undefined;
        const studentName = leadData?.name || 'Unknown Student';
        const studentNumber = leadData?.number || 'No Number';

        // Fetch assignedUserName directly from the followUp document
        let assignedUserName = data.assignedUserName || 'Unknown User';
        let assignedUserId = null;

        // Still fetch the userId for reference if needed
        if (data.userId) {
            assignedUserId = data.userId;
        }

        console.log('Assigned user name from followUp document:', assignedUserName);

        // find the assigned user of the lead using leadId
        let assignedUserIdFromLead = null;
        let assignedUserNameFromLead = 'Unknown User';
        if (leadData?.assignedTo) {
            const userDocRef = doc(db, 'users', leadData.assignedTo);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
                const userData = userDoc.data();
                assignedUserIdFromLead = userData.id || null;
                assignedUserNameFromLead = userData.name || 'Unknown User';
            }
        }

        // Fetch lead stage
        let leadStage = ' No Stage';
        console.log('Lead data:', leadData);

        // Check for various possible stage field names
        const stageValue = leadData?.stage || leadData?.leadStage || leadData?.stageId || leadData?.currentStage;

        if (stageValue) {
            console.log('Lead stage value:', stageValue);
            try {
                // First try to get stage by document ID (most common approach)
                const stageDocRef = doc(db, `companies/${companyId}/leadStages`, stageValue);
                const stageDoc = await getDoc(stageDocRef);

                if (stageDoc.exists()) {
                    const stageData = stageDoc.data();
                    console.log('Stage data found by doc ID:', stageData);
                    console.log('Stage data keys:', Object.keys(stageData));
                    console.log('Stage name options:', {
                        name: stageData.name,
                        stageName: stageData.stageName,
                        title: stageData.title,
                        label: stageData.label,
                        stage: stageData.stage
                    });

                    leadStage = stageData.stage || stageData.name || stageData.stageName || stageData.title || stageData.label || JSON.stringify(stageData) || ' No Stage';
                    console.log('Final lead stage value:', leadStage);
                } else {
                    // If not found by document ID, try querying by id field
                    const stageRef = collection(db, `companies/${companyId}/leadStages`);
                    const stageQuery = query(stageRef, where('id', '==', stageValue));
                    const stageSnapshot = await getDocs(stageQuery);

                    if (!stageSnapshot.empty) {
                        const stageData = stageSnapshot.docs[0].data();
                        console.log('Stage data found by query:', stageData);
                        console.log('Stage data keys:', Object.keys(stageData));
                        leadStage = stageData.stage || stageData.name || stageData.stageName || stageData.title || stageData.label || JSON.stringify(stageData) || ' No Stage';
                        console.log('Final lead stage value from query:', leadStage);
                    } else {
                        console.log('No stage found for:', stageValue);
                        // As a fallback, let's try to get all stages and find a match
                        const allStagesRef = collection(db, `companies/${companyId}/leadStages`);
                        const allStagesSnapshot = await getDocs(allStagesRef);
                        console.log('All stages:', allStagesSnapshot.docs.map(doc => ({ id: doc.id, data: doc.data() })));

                        // Try to find by name match as well
                        const foundStage = allStagesSnapshot.docs.find(doc =>
                            doc.id === stageValue ||
                            doc.data().id === stageValue ||
                            doc.data().name === stageValue
                        );

                        if (foundStage) {
                            const stageData = foundStage.data();
                            console.log('Stage data keys from fallback:', Object.keys(stageData));
                            leadStage = stageData.stage || stageData.name || stageData.stageName || stageData.title || stageData.label || JSON.stringify(stageData) || ' No Stage';
                            console.log('Found stage by fallback search:', stageData);
                            console.log('Final lead stage value from fallback:', leadStage);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching lead stage:', error);
                leadStage = ' No Stage';
            }
        } else {
            console.log('No stage field in lead data. Available fields:', Object.keys(leadData || {}));
        }

        return {
            id: docSnapshot.id,
            followUp: data.followUp,
            followUpTime: data.followUpTime,
            isDone: data.isDone,
            leadId: data.leadId,
            studentName,
            studentNumber,
            userId: data.userId,
            assignedUserName,
            assignedUserId,
            assignedUserNameFromLead,
            leadStage,
            isActive: leadData?.isActive ?? true
        } as FollowUp;
    };

    const handleFilterChange = async (newFilter: string) => {
        if (!currentUser || !userData || loading) return;

        try {
            // Store the filter in sessionStorage
            sessionStorage.setItem('followUpsFilter', newFilter);

            // Reset all search and filter states
            setSelectedStudent('');
            setSelectedStudentId('');
            setSelectedAssignee('');
            setSelectedAssigneeId('');
            setSearchTerm('');
            setAssigneeSearchTerm('');
            setCustomDateRange({ start: null, end: null });
            setAppliedDateRange({ start: null, end: null });

            // Set new filter
            setFilter(newFilter);

            // Reset pagination state
            setFollowUps([]);
            setLastVisible(null);
            setFirstVisible(null);
            setLastVisibleStack([]);
            setHasMore(true);
            setCurrentPage(1);

            // Clear pagination state from sessionStorage when filter changes
            clearPaginationState();

            // Prepare base query conditions
            const followUpsRef = collection(db, `companies/${userData.companyId}/followUps`);
            let conditions: QueryConstraint[] = [
                where('isDone', '==', false),
                orderBy('followUpTime', 'asc'),
                limit(pageSize)
            ];

            // Add time-based conditions based on new filter
            const now = new Date();
            if (newFilter === 'today') {
                // Show all follow-ups from today (start of day to end of day)
                const todayStart = startOfDay(now);
                const todayEnd = endOfDay(now);
                conditions.push(
                    where('followUpTime', '>=', Timestamp.fromDate(todayStart)),
                    where('followUpTime', '<=', Timestamp.fromDate(todayEnd))
                );
                setCustomDateRange({ start: todayStart, end: todayEnd });
                setAppliedDateRange({ start: todayStart, end: todayEnd });
            } else if (newFilter === 'upcoming') {
                const todayEnd = endOfDay(now);
                conditions.push(where('followUpTime', '>', Timestamp.fromDate(todayEnd)));
            } else if (newFilter === 'overdue') {
                // Show follow-ups before today (excluding today's follow-ups to prevent duplication)
                const todayStart = startOfDay(now);
                conditions.push(where('followUpTime', '<', Timestamp.fromDate(todayStart)));
            }

            // Add role-based conditions
            if (userData.isAdmin && currentScreen === 'Admin') {
                // No additional conditions needed for admin
            } else if (userData.isManager && currentScreen === 'Manager') {
                const usersQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
                const usersSnapshot = await getDocs(usersQuery);
                const users = usersSnapshot.docs.map(doc => doc.id);
                conditions.push(where('userId', 'in', users));
            } else {
                conditions.push(where('userId', '==', currentUser.uid));
            }

            // Get total count for the new filter
            const countQuery = query(followUpsRef, ...conditions.filter(c =>
                !['limit', 'startAfter'].includes(c.type)
            ));
            const countSnapshot = await getCountFromServer(countQuery);
            setTotalCount(countSnapshot.data().count);

            // Execute query
            const followUpsQuery = query(followUpsRef, ...conditions);
            const snapshot = await getDocs(followUpsQuery);

            if (!snapshot.empty) {
                setFirstVisible(snapshot.docs[0]);
                setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            }

            setHasMore(snapshot.docs.length === pageSize);

            // Process follow-up data
            const followUpsData = await Promise.all(
                snapshot.docs.map(docSnapshot =>
                    processFollowUpData(docSnapshot, userData.companyId)
                ));

            setFollowUps(followUpsData);
        } catch (error) {
            console.error('Error changing filter:', error);
            setToastMessage('Failed to change filter');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    };

    // Open whats app web in a new tab with the student's phone number
    const openWhatsAppWeb = (phoneNumber: string) => {
        const formattedNumber = phoneNumber.replace(/\D/g, ''); // Remove non-numeric characters
        const url = `https://web.whatsapp.com/send?phone=${formattedNumber}`;
        window.open(url, '_blank');
    };



    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md">
                    <div className="flex items-center gap-6 mb-8 relative">
                        <div className="flex items-center justify-center w-14 h-14 rounded-full"
                            style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}>
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/icon-follow-up-white.svg`} className='w-10 h-10' alt="Follow Up Icon" />
                        </div>
                        <h2 className="font-montserrat text-2xl font-medium text-black">Follow Ups</h2>

                        <div className="relative">
                            <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                <input
                                    id='inPageStudentSearchInput'
                                    type="text"
                                    placeholder="Search for Student"
                                    value={searchTerm || selectedStudent}
                                    onChange={handleStudentSearch}
                                    onKeyPress={handleSearch}
                                    onClick={(e) => {
                                        // Don't toggle the dropdown when clicking on the input field
                                        // This prevents accidental closing while typing/searching
                                        e.stopPropagation();
                                        if (!isStudentDropdownOpen) {
                                            toggleDropdown("student", true);
                                        }
                                    }}
                                    className={`w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl ${assigneeSearchTerm.length > 0 || selectedAssignee.length > 0 ? 'cursor-not-allowed' : ''}`}
                                    disabled={assigneeSearchTerm.length > 0 || selectedAssignee.length > 0}
                                />
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                    alt="search-lens-icon"
                                    className={`w-6 h-6 mr-2 ${assigneeSearchTerm.length > 0 || selectedAssignee.length > 0 ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                    onClick={(e) => {
                                        if (!(assigneeSearchTerm.length > 0 || selectedAssignee.length > 0)) {
                                            handleSearch(e);
                                        }
                                    }}
                                />
                            </div>

                            {isStudentDropdownOpen && (
                                <div ref={studentDropdownRef} className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 w-full max-h-[200px] overflow-y-auto">
                                    {isStudentLoading ? (
                                        <div className="absolute top-0 right-0 mt-2 mr-2">
                                            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    ) : students.length > 0 ? (
                                        students.map((student) => (
                                            <div
                                                key={student.id}
                                                className="p-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2"
                                                onClick={() => {
                                                    setSelectedStudentId(student.id);
                                                    setSelectedStudent(student.name);
                                                    setSearchTerm('');
                                                    setIsStudentDropdownOpen(false);
                                                }}
                                            >
                                                {student.name} <span className='text-sm flex items-center gap-1'><span className="text-slate-400">{student.number}</span><img src={`${process.env.PUBLIC_URL}/images/desktop/cloudTelephony.svg`} alt="transfer-lead-icon" className="w-3 h-3 cursor-pointer" /></span>
                                            </div>
                                        )))
                                        : searchTerm.length < 3 ? (
                                            <div className="p-2 text-gray-500">Type at least 3 characters to search</div>
                                        ) : (
                                            <div className="p-2 text-gray-500">No students found</div>
                                        )}
                                </div>
                            )}
                        </div>
                        {currentScreen !== 'Staff' && (
                            <div className="relative">
                                <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                    <input
                                        type="text"
                                        placeholder="Search for Assignee"
                                        value={assigneeSearchTerm || selectedAssignee}
                                        onChange={handleAssigneeSearch}
                                        onKeyPress={handleSearch}
                                        onClick={(e) => {
                                            // Don't toggle the dropdown when clicking on the input field
                                            // This prevents accidental closing while typing/searching
                                            e.stopPropagation();
                                            if (!isAssigneeDropdownOpen) {
                                                toggleDropdown("assignee", true);
                                            }
                                        }}
                                        className={`w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl ${searchTerm.length > 0 || selectedStudent.length > 0 ? 'cursor-not-allowed' : ''}`}
                                        disabled={searchTerm.length > 0 || selectedStudent.length > 0}
                                    />
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                        alt="search-lens-icon"
                                        className={`w-6 h-6 mr-2 ${searchTerm.length > 0 || selectedStudent.length > 0 ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        onClick={(e) => {
                                            if (!(searchTerm.length > 0 || selectedStudent.length > 0)) {
                                                handleSearch(e);
                                            }
                                        }}
                                    />
                                </div>

                                {isAssigneeDropdownOpen && (
                                    <div ref={assigneeDropdownRef} className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 w-full max-h-[200px] overflow-y-auto">
                                        {isAssigneeLoading ? (
                                            <div className="absolute top-0 right-0 mt-2 mr-2">
                                                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        ) : assignees.length > 0 ? (
                                            assignees.map((assignee) => (
                                                <div
                                                    key={assignee.id}
                                                    className="p-2 hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => {
                                                        setSelectedAssigneeId(assignee.id);
                                                        setSelectedAssignee(assignee.name);
                                                        setAssigneeSearchTerm('');
                                                        setIsAssigneeDropdownOpen(false);
                                                    }}
                                                >
                                                    {assignee.name}
                                                </div>
                                            )))
                                            : searchTerm.length < 3 ? (
                                                <div className="p-2 text-gray-500">Type at least 3 characters to search</div>
                                            ) : (
                                                <div className="p-2 text-gray-500">No assignee found</div>
                                            )}
                                    </div>
                                )}
                            </div>
                        )}
                        {(searchTerm || selectedStudent || assigneeSearchTerm || selectedAssignee) && (
                            <a
                                href="#"
                                className="text-xs text-blue-500 font-normal"
                                onClick={(e) => {
                                    e.preventDefault();
                                    clearSearch(e);
                                }}
                            >
                                Clear Search
                            </a>
                        )}

                        <div className="ml-auto relative">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setShowAddFollowUpModal(true)}
                                    className="px-2 py-2 bg-blue-600 text-white font-medium rounded-full hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-6 h-6" />
                                </button>
                                <div className="flex bg-gray-100 p-1 rounded-full">
                                    <button
                                        onClick={() => handleFilterChange('upcoming')}
                                        className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${filter === 'upcoming'
                                            ? 'text-white'
                                            : 'text-gray-600 hover:text-gray-800'
                                            }`}
                                        style={{
                                            background: filter === 'upcoming'
                                                ? 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)'
                                                : 'transparent'
                                        }}
                                    >
                                        Upcoming Follow Ups
                                    </button>
                                    <button
                                        onClick={() => handleFilterChange('today')}
                                        className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${filter === 'today'
                                            ? 'text-white'
                                            : 'text-gray-600 hover:text-gray-800'
                                            }`}
                                        style={{
                                            background: filter === 'today'
                                                ? 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)'
                                                : 'transparent'
                                        }}
                                    >
                                        Today's Follow Ups
                                    </button>
                                    <button
                                        onClick={() => handleFilterChange('overdue')}
                                        className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${filter === 'overdue'
                                            ? 'text-white'
                                            : 'text-gray-600 hover:text-gray-800'
                                            }`}
                                        style={{
                                            background: filter === 'overdue'
                                                ? 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)'
                                                : 'transparent'
                                        }}
                                    >
                                        Overdue Follow Ups
                                    </button>
                                </div>
                                <button
                                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                                    className="text-gray-600 hover:text-gray-800"
                                // disabled={filter === 'today'}
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                                        alt="filters-icon"
                                        className={`w-[35px] h-[35px] ${filter === 'today' ? "cursor-not-allowed" : ''}`}
                                    />
                                </button>

                                {isFilterOpen && filter !== 'today' && (
                                    <div
                                        ref={filterPopupRef}
                                        className="absolute top-0 right-0 transform translate-x-3 w-[261px] bg-white border rounded-3xl shadow-lg z-50 flex flex-col"
                                    >
                                        <div className="flex justify-between items-center p-4">
                                            <h3 className="text-[14px] font-normal leading-[20px] text-left">Date Range</h3>
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                                alt="filters-black-icon"
                                                onClick={() => setIsFilterOpen(false)}
                                                className="w-9 h-9"
                                            />
                                        </div>

                                        <div className="flex-1 px-4 pb-4">
                                            <div className="space-y-4">
                                                <div>
                                                    <span className="text-[12px] font-normal leading-[16px] mb-2 block">Start Date:</span>
                                                    <input
                                                        type="date"
                                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                                        value={customDateRange.start ? format(customDateRange.start, 'yyyy-MM-dd') : ''}
                                                        onChange={(e) => setCustomDateRange(prev => ({
                                                            ...prev,
                                                            start: e.target.value ? new Date(e.target.value) : null
                                                        }))}
                                                    />
                                                </div>
                                                <div>
                                                    <span className="text-[12px] font-normal leading-[16px] mb-2 block">End Date:</span>
                                                    <input
                                                        type="date"
                                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                                        value={customDateRange.end ? format(customDateRange.end, 'yyyy-MM-dd') : ''}
                                                        onChange={(e) => setCustomDateRange(prev => ({
                                                            ...prev,
                                                            end: e.target.value ? new Date(e.target.value) : null
                                                        }))}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4 border-t">
                                            <div className="flex justify-between space-x-3">
                                                {/* Clear Button */}
                                                <div className=''>
                                                    <button
                                                        onClick={clearFilters}
                                                        className="px-8 py-2 text-white bg-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                                        disabled={loading}
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                                <div className=''>
                                                    {/* Apply Button */}
                                                    <GradientSubmitButton
                                                        type="button"
                                                        onClick={applyCustomDateRange}
                                                        loading={loading}
                                                        label="Apply"
                                                        loadingLabel="Applying..."
                                                        disabled={!customDateRange.start || !customDateRange.end || loading}
                                                        className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                    />
                                                </div>
                                            </div>
I
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Follow Ups Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date & Time</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Follow Up</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned By</th>
                                    <th className="px-6 py-3 text-left text-xs fon-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {followUps.map((followUp) => (
                                    <tr
                                        key={followUp.id}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {followUp.followUpTime.toDate().toLocaleDateString('en-IN', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric'
                                            })} {followUp.followUpTime.toDate().toLocaleTimeString('en-IN', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                            })}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                                            <div>
                                                {expandedFollowUps.has(followUp.id) ? (
                                                    <div
                                                        className="whitespace-normal cursor-pointer"
                                                        onClick={() => {
                                                            const newExpandedSet = new Set(expandedFollowUps);
                                                            newExpandedSet.delete(followUp.id);
                                                            setExpandedFollowUps(newExpandedSet);
                                                        }}
                                                    >
                                                        {followUp.followUp}
                                                        <div className="text-blue-500 text-xs mt-1 italic">Click to collapse</div>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="truncate cursor-pointer"
                                                        onClick={() => {
                                                            const newExpandedSet = new Set(expandedFollowUps);
                                                            newExpandedSet.add(followUp.id);
                                                            setExpandedFollowUps(newExpandedSet);
                                                        }}
                                                    >
                                                        {followUp.followUp.length > 80 ?
                                                            `${followUp.followUp.substring(0, 80)}...` :
                                                            followUp.followUp
                                                        }
                                                        {followUp.followUp.length > 80 && (
                                                            <div className="text-blue-500 text-xs mt-1 italic">Click to expand</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td
                                            className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${(!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                ? 'cursor-not-allowed opacity-50'
                                                : 'cursor-pointer'
                                                }`}
                                            onClick={() => {
                                                if (!followUp.isActive && isStaff && currentScreen === 'Staff') {
                                                    return; // Disable click for inactive leads when user is staff
                                                }
                                                handleStudentClick(followUp.leadId);
                                            }}
                                            title={
                                                (!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                    ? 'This lead is disabled'
                                                    : ''
                                            }
                                        >
                                            {followUp.studentName}
                                        </td>
                                        <td
                                            className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${(!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                ? 'cursor-not-allowed opacity-50'
                                                : 'cursor-pointer'
                                                }`}
                                            onClick={() => {
                                                if (!followUp.isActive && isStaff && currentScreen === 'Staff') {
                                                    return; // Disable phone number click for inactive leads when user is staff
                                                }
                                                copyPhoneNumberToClipboard(followUp.studentNumber);
                                            }}
                                            title={
                                                (!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                    ? 'This lead is disabled'
                                                    : 'Copy Phone Number'
                                            }
                                        >
                                            {followUp.studentNumber}
                                        </td>

                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {followUp.leadStage}
                                        </td>

                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {followUp.assignedUserNameFromLead}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {followUp.assignedUserName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center">
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                                                    alt="edit-icon"
                                                    className={`w-7 h-7 ${(!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                        ? 'cursor-not-allowed opacity-50'
                                                        : 'cursor-pointer'
                                                        }`}
                                                    onClick={() => {
                                                        if (!followUp.isActive && isStaff && currentScreen === 'Staff') {
                                                            return; // Disable edit for inactive leads when user is staff
                                                        }
                                                        openEditModal(followUp);
                                                    }}
                                                    title={
                                                        (!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                            ? 'This lead is disabled'
                                                            : 'Edit Follow-up'
                                                    }
                                                />
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/mark-as-done.svg`}
                                                    alt="mark-as-done"
                                                    className={`w-7 h-7 ml-2 ${(!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                        ? 'cursor-not-allowed opacity-50'
                                                        : 'cursor-pointer'
                                                        }`}
                                                    onClick={() => {
                                                        if (!followUp.isActive && isStaff && currentScreen === 'Staff') {
                                                            return; // Disable mark as done for inactive leads when user is staff
                                                        }
                                                        openConfirmModal(followUp);
                                                    }}
                                                    title={
                                                        (!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                            ? 'This lead is disabled'
                                                            : 'Mark as Done'
                                                    }
                                                />
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/copy-phone.svg`}
                                                    alt="cpoy-number"
                                                    className={`w-8 h-8 ml-2 ${(!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                        ? 'cursor-not-allowed opacity-50'
                                                        : 'cursor-pointer'
                                                        }`}
                                                    onClick={() => {
                                                        if (!followUp.isActive && isStaff && currentScreen === 'Staff') {
                                                            return; // Disable copy phone for inactive leads when user is staff
                                                        }
                                                        copyPhoneNumberToClipboard(followUp.studentNumber);
                                                    }}
                                                    title={
                                                        (!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                            ? 'This lead is disabled'
                                                            : 'Copy Phone Number'
                                                    }
                                                />
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/whatsappLogo.svg`}
                                                    alt="whatsapp-logo"
                                                    className={`w-8 h-8 ml-2 ${(!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                        ? 'cursor-not-allowed opacity-50'
                                                        : 'cursor-pointer'
                                                        }`}
                                                    onClick={() => {
                                                        if (!followUp.isActive && isStaff && currentScreen === 'Staff') {
                                                            return; // Disable WhatsApp for inactive leads when user is staff
                                                        }
                                                        openWhatsAppWeb(followUp.studentNumber);
                                                    }}
                                                    title={
                                                        (!followUp.isActive && isStaff && currentScreen === 'Staff')
                                                            ? 'This lead is disabled'
                                                            : 'Share on WhatsApp'
                                                    }
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between mt-6 mb-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center">
                                <label className="text-xs text-gray-500 mr-2">Rows per page:</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    className="border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>

                        {/* Navigation buttons (centered) */}
                        <div className="flex items-center gap-2">
                            {/* first */}
                            <button
                                onClick={() => fetchFollowUps('first')}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;&lt;
                            </button>

                            {/* prev */}
                            <button
                                onClick={() => fetchFollowUps('prev')}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;
                            </button>

                            {/* page number */}
                            <div className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff]">
                                {currentPage}
                            </div>

                            {/* next */}
                            <button
                                onClick={() => fetchFollowUps('next')}
                                disabled={loading || !hasMore || (currentPage === Math.ceil(totalCount / pageSize))}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &gt;
                            </button>
                        </div>

                        {/* Page count (right-aligned) */}
                        <div className="flex items-center">
                            <div className="text-xs text-gray-500 whitespace-nowrap">
                                {loading ? 'Loading...' : `(Total: ${totalCount} Follow Ups) ${currentPage} / ${Math.ceil(totalCount / pageSize)} Pages`}
                            </div>
                        </div>
                    </div>

                    {/* Add Follow Up Modal */}
                    {showAddFollowUpModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-montserrat text-gray-900">Add Follow Up</h2>
                                    <button
                                        onClick={() => {
                                            setShowAddFollowUpModal(false); setFollowUpDescription(''); setFollowUpDateTime('');
                                            setSelectedStudentInModal(''); setSelectedStudentInModalId(''); setSearchTermInModal('');
                                            setIsInModalStudentDropdownOpen(false); setToggleAddFollowUpAction('add');
                                        }}>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                                            alt="Close"
                                            className="w-6 h-6"
                                        />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {/* Student Details Section */}
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="flex justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500">Student Name</p>
                                                {/* Search dropdown for student selection */}
                                                <div className="flex items-center bg-zinc-100 min-w-60">
                                                    <input
                                                        id='inModalStudentSearchInput'
                                                        type="text"
                                                        placeholder="Search for Student"
                                                        value={searchTermInModal || selectedStudentInModal}
                                                        onChange={handleStudentSearch}
                                                        onKeyPress={handleSearch}
                                                        onClick={(e) => {
                                                            // Don't toggle the dropdown when clicking on the input field
                                                            // This prevents accidental closing while typing/searching
                                                            e.stopPropagation();
                                                            if (!isInModalStudentDropdownOpen) {
                                                                toggleDropdown("studentInModal", true);
                                                            }
                                                        }}
                                                        className={`w-full px-2 py-1 text-sm text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl`}
                                                    />
                                                </div>
                                                {isInModalStudentDropdownOpen && (
                                                    <div ref={studentInModalDropdownRef} className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-[200px] overflow-y-auto">
                                                        {isStudentLoading ? (
                                                            <div className="absolute top-0 right-0 mt-2 mr-2">
                                                                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                                            </div>
                                                        ) : students.length > 0 ? (
                                                            students.map((student) => (
                                                                <div
                                                                    key={student.id}
                                                                    className="p-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2"
                                                                    onClick={() => handleStudentSelectInModal(student)}
                                                                >
                                                                    {student.name} <span className='text-sm flex items-center gap-1'><span className="text-slate-400">{student.number}</span><img src={`${process.env.PUBLIC_URL}/images/desktop/cloudTelephony.svg`} alt="transfer-lead-icon" className="w-3 h-3 cursor-pointer" /></span>
                                                                </div>
                                                            )))
                                                            : searchTerm.length < 3 ? (
                                                                <div className="p-2 text-gray-500">Type at least 3 characters to search</div>
                                                            ) : (
                                                                <div className="p-2 text-gray-500">No students found</div>
                                                            )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-gray-500">Phone Number</p>
                                                <p className="text-sm font-medium text-gray-900">{selectedStudentInModalId ? students.find(s => s.id === selectedStudentInModalId)?.number : ''}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Description</label>
                                        <textarea
                                            value={followUpDescription}
                                            onChange={(e) => setFollowUpDescription(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            rows={3}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Date & Time</label>
                                        <input
                                            type="datetime-local"
                                            value={followUpDateTime}
                                            onChange={(e) => setFollowUpDateTime(e.target.value)}
                                            min={new Date().toISOString().slice(0, 16)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="flex justify-end gap-4 mt-6">
                                        {/* Cancel Button */}
                                        <button
                                            onClick={() => {
                                                setShowAddFollowUpModal(false);
                                                setFollowUpDescription('');
                                                setFollowUpDateTime('');
                                                setSelectedStudentInModal('');
                                                setSelectedStudentInModalId('');
                                                setSearchTermInModal('');
                                                setIsInModalStudentDropdownOpen(false);
                                                setToggleAddFollowUpAction('add');
                                            }}
                                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:cursor-not-allowed"
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>

                                        {/* Add/Update Button with GradientSubmitButton */}
                                        <GradientSubmitButton
                                            type="button"
                                            onClick={() => handleAddFollowUp(toogleAddFollowUpAction)}
                                            loading={loading}
                                            label={toogleAddFollowUpAction === 'add' ? 'Add Follow Up' : 'Update Follow Up'}
                                            loadingLabel={toogleAddFollowUpAction === 'add' ? 'Adding...' : 'Updating...'}
                                            disabled={loading}
                                            className="px-4 py-2 text-white rounded-lg bg-gradient-to-b from-[#3483BE] to-[#02385E]"
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}

                    {/* Edit Modal */}
                    {showEditModal && selectedFollowUp && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-montserrat text-gray-900">Edit Follow Up</h2>
                                    <button onClick={() => setShowEditModal(false)}>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                                            alt="Close"
                                            className="w-6 h-6"
                                        />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {/* Student Details Section */}
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="flex justify-between">
                                            <div>
                                                <p className="text-sm text-gray-500">Student Name</p>
                                                <p className="text-sm font-medium text-gray-900">{selectedFollowUp.studentName}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-gray-500">Phone Number</p>
                                                <p className="text-sm font-medium text-gray-900">{selectedFollowUp.studentNumber}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Description</label>
                                        <textarea
                                            value={editedFollowUp}
                                            onChange={(e) => setEditedFollowUp(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            rows={3}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Date & Time</label>
                                        <input
                                            type="datetime-local"
                                            value={editedDateTime}
                                            onChange={(e) => setEditedDateTime(e.target.value)}
                                            min={new Date().toISOString().slice(0, 16)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="flex justify-end gap-4 mt-6">
                                        {/* Cancel Button */}
                                        <button
                                            onClick={() => setShowEditModal(false)}
                                            className="px-4 py-2 text-gray-700 border rounded-3xl border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed"
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>

                                        {/* Save Changes Button */}
                                        <GradientSubmitButton
                                            type="button"
                                            onClick={handleEdit}
                                            loading={loading}
                                            label="Save Changes"
                                            loadingLabel="Saving..."
                                            disabled={loading}
                                            className="bg-cyan-600 hover:bg-cyan-700"
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}

                    {/* Confirm Mark as Done Modal */}
                    {showConfirmModal && followUpToMarkDone && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-montserrat text-gray-900">Mark Follow Up as Done</h2>
                                    <button onClick={() => setShowConfirmModal(false)}>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                                            alt="Close"
                                            className="w-6 h-6"
                                        />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    <p className="text-gray-600">
                                        Are you sure you want to mark this follow up as done?
                                    </p>
                                    <div className="mt-2">
                                        <p className="font-medium text-gray-900">Follow Up Details:</p>
                                        <p className="text-gray-600 mt-1">{followUpToMarkDone.followUp}</p>
                                        <p className="text-gray-500 text-sm mt-2">
                                            Due: {followUpToMarkDone.followUpTime.toDate().toLocaleDateString('en-IN', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric'
                                            })} {followUpToMarkDone.followUpTime.toDate().toLocaleTimeString('en-IN', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                            })}
                                        </p>
                                    </div>
                                    <div className="flex justify-end gap-4 mt-6">
                                        {/* Cancel Button */}
                                        <button
                                            onClick={() => setShowConfirmModal(false)}
                                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-3xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>

                                        {/* Mark as Done Button */}
                                        <GradientSubmitButton
                                            type="button"
                                            onClick={handleMarkAsDone}
                                            loading={loading}
                                            label="Mark as Done"
                                            loadingLabel="Marking..."
                                            disabled={loading}
                                            className="bg-cyan-600 hover:bg-cyan-700"
                                        />
                                    </div>

                                </div>
                            </div>
                        </div>
                    )}
                    {showToast && (
                        <Toast
                            message={toastMessage}
                            isError={
                                toastMessage.includes('Error') ||
                                toastMessage.includes('Cannot') ||
                                toastMessage.includes('Failed')
                            }
                            show={showToast}
                            onClose={() => setShowToast(false)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default FollowUps;

